import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiShoppingCart, FiUser, FiMenu, FiX } from 'react-icons/fi';
import logoImage from '../assets/m2elogo.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 120px;
  min-height: 120px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 700;
  color: #2563eb;
  text-decoration: none;
  
  &:hover {
    color: #1d4ed8;
  }
`;

const LogoImage = styled.img`
  height: 100px;
  width: auto;
  object-fit: contain;
`;

const LogoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 8px;
`;

const LogoTextImage = styled.img`
  height: 42px;
  width: auto;
  object-fit: contain;
`;

const Tagline = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.2;
  margin-top: 2px;
  font-style: italic;
`;

const Superscript = styled.sup`
  font-size: 0.5em;
  font-weight: 700;
  vertical-align: 0.3em;
  line-height: 0;
  margin-left: -2px;
  color: inherit;
  position: relative;
  top: -0.1em;
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
  margin: 0 40px;
  position: relative;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 45px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background-color: #f9fafb;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    background-color: #ffffff;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 18px;
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #374151;
  font-weight: 500;
  font-size: 14px;
  transition: color 0.2s ease;
  
  &:hover {
    color: #2563eb;
  }
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CartButton = styled(Link)`
  position: relative;
  padding: 8px;
  color: #374151;
  transition: color 0.2s ease;
  
  &:hover {
    color: #2563eb;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  color: #374151;
  font-weight: 500;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f3f4f6;
    color: #2563eb;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  padding: 8px;
  color: #374151;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  display: block;
  padding: 12px 0;
  color: #374151;
  font-weight: 500;
  border-bottom: 1px solid #f3f4f6;
  
  &:hover {
    color: #2563eb;
  }
`;

const MobileSearchContainer = styled.div`
  margin-bottom: 20px;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 45px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background-color: #f9fafb;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    background-color: #ffffff;
  }
`;

const Header = ({ user, isAuthenticated, onLogout, cartCount }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/books?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          <LogoImage src={logoImage} alt="M²eBooks Logo" />
          <LogoTextContainer>
            <LogoTextImage 
              src="data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='32' viewBox='0 0 180 32'%3E%3Ctext x='0' y='24' font-family='Arial, sans-serif' font-size='20' font-weight='700' fill='%232563eb'%3EM%3Ctspan font-size='12' font-weight='700' baseline-shift='super'%3E2%3C/tspan%3EeBooks.com%3C/text%3E%3C/svg%3E" 
              alt="M²eBooks.com" 
            />
            <Tagline>a multimedia publishing company</Tagline>
          </LogoTextContainer>
        </Logo>
        
        {/* <SearchContainer>
          <form onSubmit={handleSearch}>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </SearchContainer> */}
        
        <NavLinks>
          {/* <NavLink to="/books">Browse Books</NavLink> */}
          {/* <NavLink to="/categories">Categories</NavLink> */}
          {/* <NavLink to="/deals">Deals</NavLink> */}
        </NavLinks>
        
        <UserActions>
          {/* <CartButton to="/cart">
            <FiShoppingCart size={20} />
            {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
          </CartButton> */}
          
          <NavLink to="/about" style={{ marginRight: '16px' }}>About</NavLink>
          
          {isAuthenticated ? (
            <UserButton as={Link} to="/profile">
              <FiUser size={16} />
              {user?.firstName || 'User'}
            </UserButton>
          ) : (
            <UserButton as={Link} to="/login">
              <FiUser size={16} />
              Sign In
            </UserButton>
          )}
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </MobileMenuButton>
        </UserActions>
      </HeaderContent>
      
      <MobileMenu isOpen={isMobileMenuOpen}>
        {/* <MobileSearchContainer>
          <form onSubmit={handleSearch}>
            <SearchIcon />
            <MobileSearchInput
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </MobileSearchContainer> */}
        
        {/* <MobileNavLink to="/books">Browse Books</MobileNavLink> */}
        {/* <MobileNavLink to="/categories">Categories</MobileNavLink> */}
        {/* <MobileNavLink to="/deals">Deals</MobileNavLink> */}
        <MobileNavLink to="/about">About</MobileNavLink>
        
        {isAuthenticated ? (
          <>
            <MobileNavLink to="/profile">Profile</MobileNavLink>
            <MobileNavLink to="/" onClick={onLogout}>Sign Out</MobileNavLink>
          </>
        ) : (
          <>
            <MobileNavLink to="/login">Sign In</MobileNavLink>
            <MobileNavLink to="/register">Sign Up</MobileNavLink>
          </>
        )}
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
